/**
 * Batch iOS-P2 (2026-03-02) — In-app Help & Feedback bot.
 *
 * Small floating pill on authenticated candidate + employer pages.
 * Opens a chat-style panel that:
 *   1. tries to answer from a curated FAQ (realm-scoped),
 *   2. captures EVERY message to /api/support/feedback regardless,
 *   3. mirrors to PostHog (no PII) for the 6-hourly analysis.
 *
 * Non-blocking; never overlaps the A/V recorder — the parent (App
 * layout) decides when to mount this via <HelpBot suppressed={…} />.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
    ChatCircle, X, PaperPlaneRight, CheckCircle, Sparkle, WarningCircle,
    Paperclip, Image as ImageIcon,
} from "@phosphor-icons/react";

import { supportApi } from "@/lib/apiClient";

// ---------- Batch iOS-P2.1 (2026-03-02) — attachment guardrails ----------
// Kept in sync with the backend constants (support_routes.py:
// MAX_ATTACHMENTS / MAX_ATTACHMENT_BYTES / ALLOWED_ATTACHMENT_TYPES).
// The server re-validates — these are just to give the user an
// immediate, friendly error before the network roundtrip.
const MAX_ATTACHMENTS = 3;
const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;
const ALLOWED_MIMES = new Set(["image/png", "image/jpeg", "image/jpg"]);

// ---------- Category auto-guesser (mirrors backend) ----------
const _PROBLEM_RE = /\b(bug|broken|error|failed|failing|crash|crashes|stuck|not working|doesn'?t work|does not work|can'?t|cannot|won'?t|wont|blank|missing|blocked|denied)\b/i;
const _SUGGEST_RE = /\b(feature|suggest|suggestion|would be nice|could you add|please add|wish|hope|improve|improvement|idea|proposal)\b/i;

function guessCategory(text) {
    if (_PROBLEM_RE.test(text)) return "problem";
    if (_SUGGEST_RE.test(text)) return "suggestion";
    return "question";
}

// PostHog wrapper — safe if PH isn't loaded yet.
function firePosthog(event, properties) {
    try {
        if (typeof window !== "undefined" && window.posthog?.capture) {
            window.posthog.capture(event, properties || {});
        }
    } catch { /* PH not initialised — silent */ }
}

/**
 * @param {object} props
 * @param {"candidate" | "employer"} props.realm
 * @param {string} [props.jobId]           optional context
 * @param {string} [props.applicationId]   optional context
 * @param {boolean} [props.suppressed]     hide the pill entirely (e.g. during A/V recording)
 */
export default function HelpBot({ realm, jobId, applicationId, suppressed = false }) {
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const [faq, setFaq] = useState([]);
    const [msg, setMsg] = useState("");
    const [category, setCategory] = useState("question");
    const [userCategoryOverride, setUserCategoryOverride] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    // Batch iOS-P2.1 (2026-03-02) — screenshot attachments. Up to 3
    // PNG/JPG, ≤5 MB each. `files` holds File objects the user picked;
    // `previews` holds { url, name, size } for thumbnail rendering.
    // Uploads happen INSIDE handleSend so the network activity never
    // blocks typing or the AV recorder.
    const [files, setFiles] = useState([]);
    const [previews, setPreviews] = useState([]);
    const fileInputRef = useRef(null);
    // Revoke object URLs on unmount / replacement to avoid leaks.
    useEffect(() => () => previews.forEach((p) => URL.revokeObjectURL(p.url)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []);
    // Post-submit state: shows FAQ answer (if any) + the "thanks —
    // we've logged this" confirmation. The user can close the panel
    // or send another message.
    const [lastResult, setLastResult] = useState(null); // { faq, id } | null
    const textareaRef = useRef(null);

    // Lazy-load FAQ on first open — one request per session.
    useEffect(() => {
        if (open && faq.length === 0) {
            supportApi.faq(realm).then(setFaq).catch(() => setFaq([]));
        }
    }, [open, realm, faq.length]);

    // Focus textarea when panel opens.
    useEffect(() => {
        if (open && textareaRef.current) {
            setTimeout(() => textareaRef.current?.focus(), 60);
        }
    }, [open]);

    // Auto-guess category as the user types, unless they've picked one manually.
    useEffect(() => {
        if (!userCategoryOverride) setCategory(guessCategory(msg));
    }, [msg, userCategoryOverride]);

    const pageRoute = useMemo(() => location.pathname + (location.search || ""), [location]);

    const canSend = msg.trim().length >= 2 && !submitting;

    // Batch iOS-P2.1 — file picker handler. Validates client-side for
    // an immediate friendly error, but the SERVER is the source of
    // truth (support_routes._looks_like_image + size + type checks
    // re-run on every byte). Never mutates `files` if any picked file
    // fails — an all-or-nothing add so the user knows exactly what
    // slots they're using.
    function onPickFiles(fileList) {
        const picked = Array.from(fileList || []);
        if (!picked.length) return;
        // Cap the total set to MAX_ATTACHMENTS.
        const room = MAX_ATTACHMENTS - files.length;
        if (room <= 0) {
            toast.error(`Max ${MAX_ATTACHMENTS} images per message.`);
            return;
        }
        if (picked.length > room) {
            toast.error(`You can add ${room} more image${room === 1 ? "" : "s"} (max ${MAX_ATTACHMENTS} total).`);
            return;
        }
        for (const f of picked) {
            if (!ALLOWED_MIMES.has(f.type)) {
                toast.error(`"${f.name}" isn't a PNG or JPG.`);
                return;
            }
            if (f.size > MAX_ATTACHMENT_BYTES) {
                toast.error(`"${f.name}" is over 5 MB.`);
                return;
            }
        }
        const nextFiles = [...files, ...picked];
        const nextPreviews = [
            ...previews,
            ...picked.map((f) => ({
                url: URL.createObjectURL(f),
                name: f.name,
                size: f.size,
            })),
        ];
        setFiles(nextFiles);
        setPreviews(nextPreviews);
    }

    function removeFile(i) {
        const nextFiles = files.filter((_, idx) => idx !== i);
        const nextPreviews = previews.filter((_, idx) => idx !== i);
        // Revoke the removed URL so we don't leak.
        const removed = previews[i];
        if (removed) try { URL.revokeObjectURL(removed.url); } catch { /* ignore */ }
        setFiles(nextFiles);
        setPreviews(nextPreviews);
    }

    async function handleSend() {
        if (!canSend) return;
        setSubmitting(true);
        try {
            const payload = {
                realm,
                category,
                message: msg.trim(),
                page_route: pageRoute,
                job_id: jobId || null,
                application_id: applicationId || null,
                device_ua: (typeof navigator !== "undefined" ? navigator.userAgent : "").slice(0, 400),
            };
            // Batch iOS-P2.1 — multipart only when files present; JSON
            // path stays unchanged for zero-attachment sends.
            const res = files.length
                ? await supportApi.submitWithAttachments(payload, files)
                : await supportApi.submit({ ...payload, bot_answer_shown: null });
            // Mirror to PostHog — server-authored properties, always
            // matches the DB row shape (and now carries attachment_count).
            if (res?.posthog_event?.event) {
                firePosthog(res.posthog_event.event, res.posthog_event.properties || {});
            }
            setLastResult({
                faq: res?.faq || null,
                id: res?.id,
                attachmentCount: res?.attachment_count || 0,
            });
            // Clear the composer + previews. URL.revokeObjectURL happens
            // in the unmount effect for anything still alive.
            previews.forEach((p) => { try { URL.revokeObjectURL(p.url); } catch { /* ignore */ } });
            setFiles([]);
            setPreviews([]);
            setMsg("");
            setUserCategoryOverride(false);
        } catch (e) {
            const status = e?.response?.status;
            const detail = e?.response?.data?.detail;
            if (status === 429) {
                toast.error(detail?.message || "You've sent a lot of messages — try again in a bit.");
            } else if (status === 413 || status === 415 || status === 422) {
                toast.error(typeof detail === "string" ? detail : (detail?.message || "That image can't be uploaded."));
            } else if (status === 401) {
                toast.error("Please log in to send feedback.");
            } else {
                toast.error("Couldn't send just now — please retry.");
            }
        } finally {
            setSubmitting(false);
        }
    }

    function reset() {
        setLastResult(null);
        setMsg("");
        setCategory("question");
        setUserCategoryOverride(false);
        previews.forEach((p) => { try { URL.revokeObjectURL(p.url); } catch { /* ignore */ } });
        setFiles([]);
        setPreviews([]);
    }

    // Quick-pick pills — top 4 FAQ questions for this realm.
    const suggestedFaq = useMemo(
        () => faq.slice(0, 4),
        [faq],
    );

    if (suppressed) return null;

    return (
        <div
            data-testid="help-bot-root"
            /* FIX 3 (Batch A7, 2026-03-02) — on mobile the primary CTA
               (Next / Submit / Continue) sits at the bottom-right of the
               viewport (Kanban buttons + AV recorder frame). The old
               `bottom-4 right-4` placed the Help pill on top of it.
               Lift the pill to `bottom-24` on mobile so it clears a full
               tap-target for the primary CTA; keep the desktop position
               unchanged. Same restoration applies when the panel is
               open — the panel already scales to viewport height. */
            className="fixed bottom-24 sm:bottom-4 right-4 z-[60] pointer-events-none"
            style={{ maxWidth: "calc(100vw - 24px)" }}
        >
            {open ? (
                <div
                    data-testid="help-bot-panel"
                    className="pointer-events-auto w-[360px] max-w-[calc(100vw-24px)] bg-background border border-border rounded-lg shadow-2xl overflow-hidden flex flex-col"
                    style={{ height: "min(520px, calc(100vh - 32px))" }}
                    role="dialog"
                    aria-label="Help and feedback"
                >
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/30">
                        <div className="flex items-center gap-2">
                            <Sparkle size={16} weight="fill" className="text-primary" />
                            <div className="text-sm font-semibold">Help & feedback</div>
                        </div>
                        <button
                            data-testid="help-bot-close"
                            onClick={() => { setOpen(false); reset(); }}
                            className="text-muted-foreground hover:text-foreground rounded-sm p-1"
                            aria-label="Close help panel"
                        >
                            <X size={16} weight="bold" />
                        </button>
                    </div>

                    {/* Body */}
                    <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
                        {lastResult ? (
                            <PostSubmitPanel
                                lastResult={lastResult}
                                onSendAnother={() => reset()}
                            />
                        ) : (
                            <PreSubmitPanel
                                realm={realm}
                                suggestedFaq={suggestedFaq}
                                onPick={(entry) => {
                                    setMsg(entry.question);
                                    setCategory("question");
                                    setUserCategoryOverride(false);
                                    setTimeout(() => textareaRef.current?.focus(), 30);
                                }}
                            />
                        )}
                    </div>

                    {/* Composer */}
                    {!lastResult && (
                        <div className="border-t border-border bg-background px-3 pt-2 pb-3 space-y-2">
                            <textarea
                                ref={textareaRef}
                                data-testid="help-bot-message"
                                value={msg}
                                onChange={(e) => setMsg(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                                        e.preventDefault(); handleSend();
                                    }
                                }}
                                placeholder="Type your question, problem, or idea…"
                                className="w-full min-h-[70px] max-h-[140px] resize-y bg-muted/40 border border-border rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-primary/50"
                                rows={3}
                                maxLength={4000}
                            />

                            {/* Batch iOS-P2.1 — attachment thumbnails.
                                Rendered ABOVE the actions row so the
                                remove (×) is easy to hit without
                                accidentally tapping Send. */}
                            {previews.length > 0 && (
                                <div
                                    data-testid="help-bot-attachments"
                                    className="flex items-center gap-2 flex-wrap"
                                >
                                    {previews.map((p, i) => (
                                        <div
                                            key={`${p.name}-${i}`}
                                            data-testid={`help-bot-attachment-${i}`}
                                            className="relative w-14 h-14 border border-border rounded-sm overflow-hidden bg-muted"
                                        >
                                            <img
                                                src={p.url}
                                                alt={p.name}
                                                className="w-full h-full object-cover"
                                            />
                                            <button
                                                type="button"
                                                data-testid={`help-bot-attachment-remove-${i}`}
                                                onClick={() => removeFile(i)}
                                                className="absolute top-0 right-0 bg-background/90 text-foreground rounded-bl-sm px-1 py-0.5 text-[10px] leading-none hover:bg-destructive hover:text-destructive-foreground"
                                                aria-label={`Remove ${p.name}`}
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-1.5">
                                    <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Type</label>
                                    <select
                                        data-testid="help-bot-category"
                                        value={category}
                                        onChange={(e) => { setCategory(e.target.value); setUserCategoryOverride(true); }}
                                        className="text-xs bg-background border border-border rounded-sm px-1.5 py-1"
                                    >
                                        <option value="question">Question</option>
                                        <option value="problem">Problem</option>
                                        <option value="suggestion">Suggestion</option>
                                    </select>
                                    {/* Batch iOS-P2.1 — attach button. Hidden
                                        native input; icon-button triggers it. */}
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/png,image/jpeg"
                                        multiple
                                        data-testid="help-bot-file-input"
                                        onChange={(e) => {
                                            onPickFiles(e.target.files);
                                            // Reset the input so the same file can be re-picked
                                            // after a remove.
                                            e.target.value = "";
                                        }}
                                        className="hidden"
                                    />
                                    <button
                                        type="button"
                                        data-testid="help-bot-attach"
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={files.length >= MAX_ATTACHMENTS || submitting}
                                        className="text-muted-foreground hover:text-foreground border border-border hover:border-primary/40 rounded-sm p-1 disabled:opacity-40"
                                        title={`Attach a screenshot (PNG/JPG, max ${MAX_ATTACHMENTS})`}
                                        aria-label="Attach a screenshot"
                                    >
                                        <Paperclip size={12} weight="bold" />
                                    </button>
                                    {files.length > 0 && (
                                        <span
                                            data-testid="help-bot-attach-count"
                                            className="text-[10px] text-muted-foreground"
                                        >
                                            {files.length}/{MAX_ATTACHMENTS}
                                        </span>
                                    )}
                                </div>
                                <button
                                    data-testid="help-bot-send"
                                    onClick={handleSend}
                                    disabled={!canSend}
                                    className="inline-flex items-center gap-1.5 text-xs font-semibold bg-primary text-primary-foreground rounded-sm px-3 py-1.5 disabled:opacity-40 hover:opacity-90"
                                >
                                    <PaperPlaneRight size={12} weight="bold" />
                                    {submitting ? "Sending…" : "Send"}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <button
                    data-testid="help-bot-open"
                    onClick={() => setOpen(true)}
                    className="pointer-events-auto inline-flex items-center gap-1.5 bg-primary text-primary-foreground shadow-lg hover:shadow-xl rounded-full pl-3 pr-4 py-2 text-xs font-semibold transition-transform hover:-translate-y-0.5"
                    aria-label="Open help and feedback"
                >
                    <ChatCircle size={15} weight="fill" />
                    Help
                </button>
            )}
        </div>
    );
}

function PreSubmitPanel({ realm, suggestedFaq, onPick }) {
    return (
        <div className="space-y-3" data-testid="help-bot-pre-panel">
            <div className="text-xs text-muted-foreground leading-relaxed">
                {realm === "employer"
                    ? "Ask a question, report a problem, or share an idea. Our team reads every message."
                    : "Stuck? Tell us what's happening — our team reads every message and gets back to you."}
            </div>
            {suggestedFaq.length > 0 && (
                <div className="space-y-1.5">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Common questions</div>
                    <ul className="space-y-1" data-testid="help-bot-suggested">
                        {suggestedFaq.map((entry) => (
                            <li key={entry.id}>
                                <button
                                    data-testid={`help-bot-faq-${entry.id}`}
                                    onClick={() => onPick(entry)}
                                    className="w-full text-left text-xs bg-muted/40 hover:bg-muted rounded-sm px-2.5 py-1.5 border border-transparent hover:border-border transition-colors"
                                >
                                    {entry.question}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

function PostSubmitPanel({ lastResult, onSendAnother }) {
    return (
        <div className="space-y-3" data-testid="help-bot-post-panel">
            <div
                data-testid="help-bot-thanks"
                className="flex items-start gap-2 border border-emerald-200 bg-emerald-50 rounded-md px-3 py-2"
            >
                <CheckCircle size={16} weight="fill" className="text-emerald-600 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-emerald-900">
                    {lastResult.faq
                        ? "Thanks — we've logged this and the team will follow up if needed."
                        : "We've passed this to the team — we'll get back to you."}
                    {lastResult.attachmentCount > 0 && (
                        <span
                            data-testid="help-bot-thanks-attachments"
                            className="ml-1 opacity-80"
                        >
                            ({lastResult.attachmentCount} screenshot
                            {lastResult.attachmentCount === 1 ? "" : "s"} attached)
                        </span>
                    )}
                </div>
            </div>

            {lastResult.faq && (
                <div
                    data-testid="help-bot-faq-answer"
                    className="border border-border rounded-md p-3 bg-muted/20"
                >
                    <div className="text-[10px] uppercase tracking-widest text-primary font-semibold mb-1">
                        Quick answer
                    </div>
                    <div className="text-xs font-semibold mb-1">{lastResult.faq.question}</div>
                    <div className="text-xs text-foreground/85 leading-relaxed">
                        {lastResult.faq.answer}
                    </div>
                </div>
            )}

            {!lastResult.faq && (
                <div
                    data-testid="help-bot-no-match"
                    className="flex items-start gap-2 border border-border rounded-md px-3 py-2"
                >
                    <WarningCircle size={14} weight="fill" className="text-muted-foreground flex-shrink-0 mt-0.5" />
                    <div className="text-[11px] text-muted-foreground">
                        We couldn&apos;t answer this instantly, but your message reached the team — we&apos;ll follow up over email.
                    </div>
                </div>
            )}

            <button
                data-testid="help-bot-send-another"
                onClick={onSendAnother}
                className="w-full text-xs border border-border hover:border-primary/50 rounded-sm px-3 py-2 text-muted-foreground hover:text-foreground"
            >
                Send another message
            </button>
        </div>
    );
}
